/**
 * ניהול המשרד — the office's own administration home. OA-0001 §3.2.
 *
 *   > *"The GL-POINT operator must NOT be required to decide 'which employee of this customer may see
 *   > Company X?' That decision belongs to the CUSTOMER. GL-POINT supplies the authorization system;
 *   > the customer operates it."*
 *
 * Every tile here is a NOUN and a place (09 §1.3: a noun is a screen, a verb is an action), and the
 * ORDER of the first three is the one thing worth defending in review. They are three different
 * questions with three different tables and three different capabilities, and OA-0001 §3.7/§3.8 is
 * that they never collapse into one admin flag:
 *
 *   משתמשים        who exists in the office            `tenant.user.manage` / `tenant.user.invite`
 *   תפקידים        what a user may DO                  `tenant.role.manage`
 *   הרשאות לחברות  which companies they may do it ON   `companies.assignment.manage`
 *
 * D-12 is what happens when the third one is faked: Legacy's admin console wrote
 * `tenant_company.assigned_users`, NO AUTHORIZATION PATH READ IT, and the console reported success
 * while access did not change. Every tile below leads to a screen over a table with a real reader.
 *
 * THE TILES ARE NOT A PERMISSION CHECK. A user who may not open one of these screens is refused by
 * the gate, and the refusal arrives as a written Hebrew denial. A hidden tile is presentation; it is
 * never the control.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { routes } from '../routes';

const TITLE_HE = 'ניהול המשרד';

/**
 * THE OFFICE'S OWN NAME. **The last write in this app with no control at all.**
 *
 * `tenant.branding.update` was one of the six PUTs G-11 names and the only one of the six that
 * nothing called: this screen DISPLAYED the office's branding and offered no way to change it, so
 * an office could not set its own name.
 *
 * ── AND BRANDING IS CONFIGURATION, NEVER AN ACCESS INPUT ───────────────────────────────────────
 *
 * OA-0001 §3.13, and the route says the same: branding is never derived from the logged-in
 * username, never determines company access, and never justifies a second Tenant just to show
 * another logo. That is worth stating ON THE FORM, because "our other office needs its own logo" is
 * exactly how a second tenant gets created and a client list gets split in two.
 *
 * The 409 is a REAL outcome, not an error: two administrators editing at once. It arrives through
 * the ordinary classification, so the person is told the office was changed by somebody else rather
 * than being shown their own text as though it had been saved.
 */
type BrandingOutcome =
  | { readonly kind: 'saved' }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

interface AdminParent {
  readonly titleHe: string;
  readonly leadHe: string;
  readonly href: string;
}

const ADMIN_PARENTS: readonly AdminParent[] = [
  {
    titleHe: 'משתמשים',
    leadHe: 'מי קיים במשרד, מי הוזמן וטרם הצטרף, ומי הושבת.',
    href: routes.tenantUsers,
  },
  {
    titleHe: 'תפקידים והרשאות',
    leadHe: 'מה מותר לעשות. תפקיד לעולם אינו מקנה גישה לחברה כלשהי.',
    href: routes.tenantRoles,
  },
  {
    // OA-0001 §3.2: the customer OPERATES the authorization system, which means defining roles and
    // not only choosing from the five the office was created with.
    titleHe: 'הגדרת תפקידים',
    leadHe: 'הרכבת תפקיד מהרשאות, ועריכת תפקיד קיים. תפקיד הוא רשימת ההרשאות שמתחתיו.',
    href: routes.tenantRoleAuthoring,
  },
  {
    titleHe: 'הרשאות לחברות',
    leadHe: 'אילו חברות כל משתמש רשאי לפתוח. גישה היא תמיד הענקה מפורשת.',
    href: routes.tenantCompanyAccess,
  },
  {
    titleHe: 'חברות במשרד',
    leadHe: 'רשימת החברות, הקמת חברה חדשה, והמכסה שנרכשה.',
    href: routes.tenantCompanies,
  },
  {
    titleHe: 'מנוי ומכסות',
    leadHe: 'מה נרכש ומה בשימוש.',
    href: routes.tenantSubscription,
  },
  {
    titleHe: 'מקורות נתונים',
    leadHe: 'החיבורים למערכות חיצוניות, מצבם וטריות הנתונים שמגיעים מהם.',
    href: routes.tenantSources,
  },
  {
    titleHe: 'אבטחה ויומן ביקורת',
    leadHe: 'פעולות שבוצעו במשרד, ניסיונות שנדחו, והרשאות תמיכה.',
    href: routes.tenantSecurity,
  },
  {
    // Identity belongs to the ACCOUNT, not to the office. The tile is here because this is where an
    // office user looks, and the screen itself renders on either surface.
    titleHe: 'האבטחה שלי',
    leadHe: 'איפה החשבון שלך מחובר, ניתוק התחברויות, ואימות דו-שלבי.',
    href: routes.personalSecurity,
  },
];

export function TenantAdminScreen(): JSX.Element {
  const ctx = useWorkContext();
  const [displayNameHe, setDisplayNameHe] = useState('');
  const [sending, setSending] = useState(false);
  const [branding, setBranding] = useState<BrandingOutcome | null>(null);

  async function saveBranding(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSending(true);
    setBranding(null);
    try {
      // `logoAssetRef` is deliberately NOT sent. There is no asset upload in this product yet, and
      // a field that posted an empty string would clear a mark somebody set elsewhere.
      await callOperation('tenant.branding.update', { body: { displayNameHe } });
      setBranding({ kind: 'saved' });
    } catch (err: unknown) {
      setBranding({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
    }
  }

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="TENANT" actual={ctx.surface} />;
  }

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'החברות שלי', href: routes.companies }}
      provenanceHe="ניהול המשרד נעשה בידי המשרד עצמו. הפלטפורמה אינה מחליטה מי במשרד רשאי לראות אילו חברות."
    >
      <section className="tiles">
        {ADMIN_PARENTS.map((parent) => (
          <article key={parent.titleHe} className="tile">
            <h2 className="tile__title">{parent.titleHe}</h2>
            <p className="tile__lead">{parent.leadHe}</p>
            <a className="btn btn--primary" href={parent.href}>
              פתיחה
            </a>
          </article>
        ))}
      </section>

      {/* ── THE OFFICE'S OWN NAME. See `BrandingOutcome` above for why this form exists. ── */}
      <form className="form" onSubmit={(e) => void saveBranding(e)}>
        <h2 className="form__title">שם המשרד כפי שהוא מוצג</h2>
        <label className="field">
          <span className="field__label">שם לתצוגה</span>
          <input
            className="field__input"
            name="displayNameHe"
            value={displayNameHe}
            onChange={(e) => setDisplayNameHe(e.currentTarget.value)}
            required
          />
          <span className="field__hint">
            השם מוצג למשתמשי המשרד. הוא אינו קובע מי רשאי לראות אילו חברות, ואינו נגזר משם המשתמש
            שמחובר. אין צורך במשרד נוסף כדי להציג שם או סמל אחר.
          </span>
        </label>

        <div className="actions-row">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={sending || displayNameHe.trim() === ''}
          >
            {sending ? 'שומר…' : 'שמירת השם'}
          </button>
        </div>
      </form>

      {branding === null ? null : branding.kind === 'saved' ? (
        <StateBlock
          titleHe="השם נשמר"
          bodyHe="השם החדש יוצג למשתמשי המשרד. הרשאות וגישה לחברות לא השתנו."
        />
      ) : (
        <ActionFailureNotice
          failure={branding.failure}
          renderConflict={() => (
            // 409 is two administrators editing at once, and it is a fact rather than a fault. The
            // person must not be left looking at their own text as though it had been saved.
            <StateBlock
              titleHe="השם שונה בינתיים"
              bodyHe="מישהו אחר במשרד שינה את השם בזמן העריכה, ולכן השינוי לא נשמר."
              tone="error"
              nextStep={<p>אפשר לרענן את המסך, לראות את השם הנוכחי ולהחליט מחדש.</p>}
            />
          )}
        />
      )}
    </AppShell>
  );
}
